import type { TooltipComponentOption } from 'echarts/components';

/**
 * ECharts draws to SVG/canvas and cannot read CSS custom properties, so chart
 * colors are resolved from the design-system tokens and passed in as a theme.
 */
export interface ChartTokens {
  text: string;
  subtle: string;
  divider: string;
  dividerSubtle: string;
  background: string;
  surface: string;
  layer: string;
  primary: string;
  accent: string;
  palette: string[];
  fontFamily: string;
}

const TOKEN_VARIABLES = {
  text: ['--theme_color_content_normal', '#343a3f'],
  subtle: ['--theme_color_content_subtle', 'rgba(52, 58, 63, 0.7)'],
  divider: ['--theme_color_content_divider', 'rgba(52, 58, 63, 0.16)'],
  dividerSubtle: ['--theme_color_content_divider_subtle', 'rgba(52, 58, 63, 0.08)'],
  background: ['--theme_color_background_base', '#ffffff'],
  surface: ['--theme_color_background_surface', '#eef1f2'],
  layer: ['--theme_color_background_layer', 'rgba(52, 58, 63, 0.08)'],
  primary: ['--theme_color_solid_primary', '#0072c3'],
  accent: ['--reference_palette_purple_50', '#a56eff'],
} as const;

/** Categorical series colors, in order; [CSS variable, fallback]. */
export const PALETTE_VARIABLES = [
  ['--reference_palette_cyan_50', '#1192e8'],
  ['--reference_palette_purple_50', '#a56eff'],
  ['--reference_palette_eucalyptus_40', '#28bc98'],
  ['--reference_palette_orange_40', '#fb936e'],
  ['--reference_palette_magenta_50', '#ee5396'],
  ['--reference_palette_blue_50', '#4589ff'],
  ['--reference_palette_teal_40', '#08bdba'],
  ['--reference_palette_yellow_30', '#f5bf1b'],
] as const;

export function readChartTokens(root: Element = document.documentElement): ChartTokens {
  const style = getComputedStyle(root);
  const read = ([variable, fallback]: readonly [string, string]) =>
    style.getPropertyValue(variable).trim() || fallback;
  return {
    ...(Object.fromEntries(
      Object.entries(TOKEN_VARIABLES).map(([key, token]) => [key, read(token)])
    ) as Omit<ChartTokens, 'palette' | 'fontFamily'>),
    palette: PALETTE_VARIABLES.map(read),
    fontFamily: getComputedStyle(document.body).fontFamily || 'sans-serif',
  };
}

/** Resolve one design-token CSS variable for ECharts, which cannot read CSS variables. */
export function readCssColor(
  variable: string,
  fallback: string,
  root: Element = document.documentElement
) {
  return getComputedStyle(root).getPropertyValue(variable).trim() || fallback;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Wheel zoom needs ⌘ (Ctrl off Mac) so plain scrolling scrolls the page. ECharts types
 * only list shift/ctrl/alt, but it reads `${modifier}Key` from the event, so meta works.
 */
export const ZOOM_WHEEL_MODIFIER = (isMac ? 'meta' : 'ctrl') as 'ctrl';
export const ZOOM_HINT = isMac ? '⌘ + scroll to zoom' : 'Ctrl + scroll to zoom';

/**
 * By default ECharts eases every update over 500ms and, because of that, throttles
 * dataZoom to 10 updates a second, so wheel zoom and slider drags lag behind the
 * pointer. Without update animation it follows input directly (20ms throttle).
 */
export const ZOOM_UPDATE = { animationDurationUpdate: 0 } as const;

/** Work without a project: a neutral grey, dark on light backgrounds and light on dark ones. */
export const unassignedColor = (tokens: ChartTokens) => withAlpha(tokens.text, 0.55);
/** The same grey for HTML next to a chart. */
export const UNASSIGNED_CSS_COLOR =
  'color-mix(in srgb, var(--theme_color_content_normal) 55%, transparent)';

/** Apply an alpha to a hex or rgb(a) token value. */
export function withAlpha(color: string, alpha: number): string {
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (hex) {
    const full = hex.length === 3 ? [...hex].map(c => c + c).join('') : hex;
    const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgb = color.match(/^rgba?\(([^,]+),([^,]+),([^,)]+)(?:,([^)]+))?\)$/);
  if (rgb) {
    const base = rgb[4] === undefined ? 1 : Number(rgb[4]);
    return `rgba(${rgb[1].trim()}, ${rgb[2].trim()}, ${rgb[3].trim()}, ${base * alpha})`;
  }
  return color;
}

/** Shared HTML tooltip chrome; tooltip content is plain HTML styled with tokens. */
export function tooltipFrame(tokens: ChartTokens) {
  return {
    backgroundColor: tokens.background,
    borderColor: tokens.divider,
    borderWidth: 1,
    padding: [10, 12],
    textStyle: { color: tokens.text, fontFamily: tokens.fontFamily, fontSize: 12 },
    extraCssText: 'border-radius: 8px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);',
  };
}

export function createChartTheme(tokens: ChartTokens) {
  const axis = {
    axisLine: { show: false, lineStyle: { color: tokens.divider } },
    axisTick: { show: false },
    axisLabel: { color: tokens.subtle, fontSize: 11 },
    splitLine: { lineStyle: { color: tokens.dividerSubtle, type: 'dashed' } },
  };
  return {
    color: tokens.palette,
    backgroundColor: 'transparent',
    textStyle: { color: tokens.text, fontFamily: tokens.fontFamily },
    title: { textStyle: { color: tokens.text }, subtextStyle: { color: tokens.subtle } },
    legend: { textStyle: { color: tokens.subtle, fontSize: 12 } },
    tooltip: tooltipFrame(tokens),
    categoryAxis: { ...axis, splitLine: { show: false } },
    valueAxis: axis,
    timeAxis: axis,
    bar: { itemStyle: { borderRadius: 6 } },
    line: { smooth: true, symbol: 'circle', symbolSize: 6 },
    dataZoom: {
      borderColor: 'transparent',
      backgroundColor: tokens.dividerSubtle,
      fillerColor: withAlpha(tokens.primary, 0.14),
      handleStyle: { color: tokens.background, borderColor: tokens.primary },
      moveHandleStyle: { color: tokens.primary, opacity: 0.4 },
      dataBackground: {
        lineStyle: { color: tokens.divider },
        areaStyle: { color: tokens.dividerSubtle },
      },
      selectedDataBackground: {
        lineStyle: { color: tokens.primary },
        areaStyle: { color: withAlpha(tokens.primary, 0.2) },
      },
      textStyle: { color: tokens.subtle },
    },
  };
}

/** Tooltip formatters return HTML; escape any user-provided text. */
export const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!
  );
type TooltipPositionCallback = Extract<
  NonNullable<TooltipComponentOption['position']>,
  (...args: never[]) => unknown
>;

const TOOLTIP_OFFSET = 14;

/**
 * For charts smaller than their tooltip (sparklines): always above the hovered column,
 * centred on it. Pair with `appendTo: 'body'` so the card around the chart cannot clip it.
 */
export const tooltipAboveColumn: TooltipPositionCallback = (point, _params, _el, rect, size) => {
  if (!rect) return point;
  const [width, height] = size.contentSize;
  return [rect.x + rect.width / 2 - width / 2, rect.y - height - 8];
};

/** Pins item tooltips above the hovered mark, or below it at the top edge, instead of following the pointer. */
export const anchoredTooltip: TooltipPositionCallback = (point, _params, _el, rect, size) => {
  if (!rect) return point;
  const [width, height] = size.contentSize;
  // Measure from the mark's centre: hover outlines change its stroke, not its centre.
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const left = Math.min(Math.max(0, centerX - width / 2), size.viewSize[0] - width);
  const above = centerY - height - TOOLTIP_OFFSET;
  return [left, above >= 0 ? above : centerY + TOOLTIP_OFFSET];
};
